import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ProfileFindInterface } from '@zaoblako/shared/modules/security/dtos/profile/profile-find.dto';
import { ProfileLoginFindInterface } from '@zaoblako/shared/modules/security/dtos/profile/profile-login-find.dto';
import { ProfileLoginFormInterface } from '@zaoblako/shared/modules/security/dtos/profile/profile-login-form.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { RoleEnum } from '@zaoblako/shared/modules/security/enums/role.enum';
import { ProfileService } from '@zaoblako/web/core/services/security/profile.service';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private loggedInUser: ProfileLoginFindInterface;

  public get currentProfile() {
    return this.loggedInUser;
  }

  public get currentUser(): UserFindInterface {
    return (
      this.loggedInUser?.usersPerSubdomain?.[location.hostname] ||
      (this.loggedInUser?.externalUsersPerSubdomain?.[location.hostname] as any)
    );
  }

  public get currentTypeTenant(): 'MAIN' | 'LK' {
    if (this.loggedInUser?.usersPerSubdomain?.[location.hostname]) {
      return 'MAIN';
    } else if (this.loggedInUser?.externalUsersPerSubdomain?.[location.hostname]) {
      return 'LK';
    }
    return 'MAIN';
  }

  public get getToken(): string {
    return localStorage.getItem('JWT_TOKEN');
  }

  public constructor(private readonly router: Router, private readonly profileService: ProfileService) {
    const currentUser = localStorage.getItem('JWT_USER');
    if (currentUser) {
      this.setUser(JSON.parse(currentUser));
    }
  }

  public setToken(token: string) {
    localStorage.setItem('JWT_TOKEN', token);
  }

  public login(login: ProfileLoginFormInterface): Observable<ProfileLoginFindInterface> {
    return this.profileService.login(login).pipe(
      tap((user) => {
        this.setUser(user);
      })
    );
  }

  public logout(url?: string) {
    localStorage.removeItem('JWT_TOKEN');
    localStorage.removeItem('JWT_USER');

    this.loggedInUser = undefined;
    this.router.navigate(['/login'], { queryParams: { url } }).catch(console.error);
  }

  public updateLoggedUser(user: Partial<ProfileLoginFindInterface>) {
    this.setUser({ ...this.loggedInUser, ...user });
  }

  public isAuthenticated(): Observable<ProfileLoginFindInterface> {
    if (!this.getToken) {
      return of(null);
    }
    return this.profileService.check();
  }

  public hasRole(roles: RoleEnum | RoleEnum[]): boolean {
    const currentUserRoles = this.currentProfile.usersPerSubdomain?.[location.hostname]?.roles.map((role) => role.type);
    if (!currentUserRoles) {
      return false;
    }

    return Array.isArray(roles)
      ? roles.some((role) => currentUserRoles.includes(role))
      : currentUserRoles.includes(roles);
  }

  public setUser(user: ProfileLoginFindInterface) {
    this.loggedInUser = user;
    if (this.loggedInUser?.usersPerSubdomain) {
      Object.keys(this.loggedInUser.usersPerSubdomain).forEach((key) => {
        this.loggedInUser.usersPerSubdomain[key].profile = <ProfileFindInterface>{
          id: this.loggedInUser.id,
          name: this.loggedInUser.name,
          firstName: this.loggedInUser.firstName,
          lastName: this.loggedInUser.lastName,
          middleName: this.loggedInUser.middleName,
          phone: this.loggedInUser.phone,
          email: this.loggedInUser.email,
          file: this.loggedInUser.file,
          telegramId: this.loggedInUser.telegramId,
          lastTime: this.loggedInUser.lastTime
        };
      });

      Object.keys(this.loggedInUser.externalUsersPerSubdomain).forEach((key) => {
        this.loggedInUser.externalUsersPerSubdomain[key].profile = <ProfileFindInterface>{
          id: this.loggedInUser.id,
          name: this.loggedInUser.name,
          firstName: this.loggedInUser.firstName,
          lastName: this.loggedInUser.lastName,
          middleName: this.loggedInUser.middleName,
          phone: this.loggedInUser.phone,
          email: this.loggedInUser.email,
          file: this.loggedInUser.file,
          telegramId: this.loggedInUser.telegramId,
          lastTime: this.loggedInUser.lastTime
        };
      });
    }

    localStorage.setItem('JWT_TOKEN', user.token);
    localStorage.setItem('JWT_USER', JSON.stringify(user));
  }
}
